import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import api from "../../api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const Racetracks = () => {
  document.title = "Pokémon Radiance | The Racetracks";

  // =======================
  // Racetrack & Pokemon Data
  // =======================
  const [racetrackData, setRacetrackData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [myPokemons, setMyPokemons] = useState([]);

  // ===================
  // Modals & Form State
  // ===================
  const [showAdd, setShowAdd] = useState(false);
  const [pokid, setPokid] = useState("");

  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeRaceId, setChallengeRaceId] = useState(null);
  const [challengePokid, setChallengePokid] = useState("");

  // =========================
  // Racing Animation Overlay
  // =========================
  const [showRaceAnimation, setShowRaceAnimation] = useState(false);
  const [racingResult, setRacingResult] = useState(null);
  const [raceMinipicUser, setRaceMinipicUser] = useState("");
  const [raceMinipicTrack, setRaceMinipicTrack] = useState("");
  const [user, setUser] = useState(null);

  // =========================
  // Popup Overlay (replaces alerts)
  // =========================
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("#1d222e");
  const [popupImg, setPopupImg] = useState(null);

  // =========================
  // Load user (from localStorage)
  // =========================
  useEffect(() => {
    const storedUser = localStorage.getItem("authUser");
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  // =========================
  // Load Racetracks & MyPokemons
  // =========================
  const loadRacetracks = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/v1/racetracks", {
        withCredentials: true,
      });
      setRacetrackData(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load racetrack data.");
    } finally {
      setLoading(false);
    }
  };

  const loadMyPokemons = async () => {
    try {
      const res = await api.get("/v1/my-pokemon?availableForRace=1", {
        withCredentials: true,
      });
      setMyPokemons(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadRacetracks();
    loadMyPokemons();
  }, []);

  // =========================
  // Add Pokemon to Track
  // =========================
  const handleAddPokemon = async () => {
    if (!pokid) {
      // Instead of alert
      setPopupColor("red");
      setPopupMessage("Please select a Pokémon!");
      setPopupImg(null);
      setShowPopup(true);
      return;
    }
    try {
      const res = await api.post(
        `/v1/racetracks/add?pokid=${pokid}`,
        {},
        { withCredentials: true },
      );

      // Attempt to show the Pokémon sprite in the popup if we can find it:
      let sprite = null;
      const userMon = myPokemons.find((p) => p.id === Number(pokid));
      if (userMon) {
        // e.g. if userMon.name === "Pikachu", sprite => "/sprites/Pikachu.png"
        sprite = `/sprites/${userMon.name}.png`;
      }

      // Success popup
      setPopupColor("#1d222e");
      setPopupMessage(res.data.message || "Pokémon placed successfully!");
      setPopupImg(sprite);
      setShowPopup(true);

      setShowAdd(false);
      loadRacetracks();
      loadMyPokemons();
    } catch (err) {
      console.error(err);

      // Error popup
      setPopupColor("red");
      setPopupMessage(
        err.response?.data?.detail || "Error adding Pokémon to track.",
      );
      setPopupImg(null);
      setShowPopup(true);
    }
  };

  // =========================
  // Challenge Flow
  // =========================
  const handleChallenge = (raceId) => {
    setChallengeRaceId(raceId);
    setShowChallenge(true);
  };

  const confirmChallenge = async () => {
    if (!challengePokid) return;
    setShowChallenge(false);

    // 1) Fetch track entry
    const trackEntry = racetrackData.find((r) => r.id === challengeRaceId);
    if (!trackEntry) {
      setPopupColor("red");
      setPopupMessage("Track entry not found");
      setPopupImg(null);
      setShowPopup(true);
      return;
    }
    // Set track's minipic
    setRaceMinipicTrack(trackEntry.minipic || "/img/placeholder.png");

    // 2) My Pokémon minipic (if we have it)
    const userMon = myPokemons.find((m) => m.id === parseInt(challengePokid));
    if (userMon && userMon.minipic) {
      setRaceMinipicUser(userMon.minipic);
    } else {
      setRaceMinipicUser("/img/placeholder.png");
    }

    // 3) Call server to get race outcome
    try {
      const res = await api.post(
        `/v1/racetracks/${challengeRaceId}/challenge`,
        parseInt(challengePokid),
        { withCredentials: true },
      );
      setRacingResult(res.data);
      setShowRaceAnimation(true);

      // Reload data
      loadRacetracks();
      loadMyPokemons();
    } catch (err) {
      console.error(err);
      setPopupColor("red");
      setPopupMessage(
        err.response?.data?.detail || "Error challenging Pokémon.",
      );
      setPopupImg(null);
      setShowPopup(true);
    }
  };

  // Called after the racing motion finishes
  const handleAnimationComplete = () => {
    // (Optional)
  };

  // Close the racing overlay
  const closeRacingOverlay = () => {
    setShowRaceAnimation(false);
    setRacingResult(null);
    setRaceMinipicUser("");
    setRaceMinipicTrack("");
  };

  // =========================
  // Utility: Format Pokémon Name
  // =========================
  const typedPrefixes = ["Rainbow", "Cursed", "Shadow", "Golden", "diamond"];
  const formatPokemonName = (name) => {
    for (let prefix of typedPrefixes) {
      if (name.startsWith(prefix)) {
        return prefix + " " + name.slice(prefix.length);
      }
    }
    return name;
  };

  const isColorPokemon = (name) => {
    // We only allow typed color Pokemon on the race?
    return typedPrefixes.some((prefix) => name.startsWith(prefix));
  };
  const trackEntry = racetrackData.find((r) => r.id === challengeRaceId);
  const trackName = trackEntry?.pokname || "";
  // =========================
  // RENDER
  // =========================
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Mini Games" breadcrumbItem="Racetracks" />

          <Row className="justify-content-center">
            <Col xs={12} lg={12}>
              <Card>
                <CardBody>
                  <div className="text-center mb-4">
                    <img
                      src="/Minigames/race.gif"
                      alt="Race"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  </div>

                  <h4 className="text-center mb-3">
                    Welcome to the Racetracks!
                  </h4>
                  <div style={{ lineHeight: "1.6" }}>
                    <ul>
                      <li>
                        <strong>Risky Race:</strong> Lose a race? You forfeit
                        your Pokémon to the winner.
                      </li>
                      <li>
                        <strong>Higher Level Advantage:</strong> Higher-level
                        Pokémon have better odds.
                      </li>
                      <li>
                        <strong>Typed Pokémon:</strong> (e.g.{" "}
                        <em>Cursed, Shadow, Golden</em>) can only race against
                        the same typed prefix.
                      </li>
                      <li>
                        In some cases, more than two people might challenge the
                        same Pokémon, resulting in multiple races in quick
                        succession.
                      </li>
                    </ul>
                    <p className="text-danger">
                      <strong>Note:</strong> Pokémon will not be returned under
                      any circumstances, so race at your own risk!
                    </p>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap">
                    <h5 className="mb-2">Active Racetrack Entries</h5>
                    <Button color="primary" onClick={() => setShowAdd(true)}>
                      Add Pokémon
                    </Button>
                  </div>

                  {loading && (
                    <div className="mt-3">Loading racetrack data...</div>
                  )}
                  {error && <div className="text-danger mt-3">{error}</div>}

                  {!loading && !error && (
                    <div className="table-responsive mt-3">
                      <Table striped>
                        <thead>
                          <tr>
                            <th>-</th>
                            <th>Pokémon</th>
                            <th>Level</th>
                            <th>Trainer</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {racetrackData.map((entry) => (
                            <tr key={entry.id}>
                              <td>
                                <img
                                  src={"/icon/" + entry.pokname + ".gif"}
                                  alt=""
                                  style={{ maxWidth: "40px", height: "auto" }}
                                />
                              </td>
                              <td>{formatPokemonName(entry.pokname)}</td>
                              <td>{entry.level}</td>
                              <td>
                                <Link
                                  to={`/playerprofile?user=${entry.owner_name}`}
                                  style={{ textDecoration: "none" }}
                                >
                                  {entry.owner_name}
                                </Link>
                              </td>
                              <td>
                                {entry.owner_name === user?.username ? (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    disabled
                                    title="You cannot race against your own Pokémon"
                                  >
                                    Own Pokémon
                                  </Button>
                                ) : (
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => handleChallenge(entry.id)}
                                  >
                                    Race Pokémon
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ADD POKEMON MODAL */}
      <Modal isOpen={showAdd} toggle={() => setShowAdd(false)}>
        <ModalHeader toggle={() => setShowAdd(false)}>
          Add Pokémon to Racetracks
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Pokémon</Label>
            <Input
              type="select"
              value={pokid}
              onChange={(e) => setPokid(e.target.value)}
            >
              <option value="">-- Select a Pokémon --</option>
              {myPokemons
                .filter((pkm) => isColorPokemon(pkm.name))
                .map((pkm) => (
                  <option key={pkm.id} value={pkm.id}>
                    {formatPokemonName(pkm.name)} Level {pkm.level}
                  </option>
                ))}
            </Input>
          </FormGroup>
          <small className="text-muted">
            Note: You are risking losing the Pokémon that participates in the
            race.
          </small>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleAddPokemon}>
            Add
          </Button>
        </ModalFooter>
      </Modal>

      {/* CHALLENGE MODAL */}
      <Modal isOpen={showChallenge} toggle={() => setShowChallenge(false)}>
        <ModalHeader toggle={() => setShowChallenge(false)}>
          Challenge a Pokémon
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Your Pokémon</Label>
            <Input
              type="select"
              value={challengePokid}
              onChange={(e) => setChallengePokid(e.target.value)}
            >
              <option value="">-- Select a Pokémon --</option>

              {myPokemons
                // 2) Optional: keep only typed color pokémon (if needed)
                .filter((pkm) => isColorPokemon(pkm.name))

                // 3) Only show Pokémon with the same name/prefix as the track’s Pokémon
                .filter((pkm) => pkm.name === trackName)

                .map((pkm) => (
                  <option key={pkm.id} value={pkm.id}>
                    {formatPokemonName(pkm.name)} Level {pkm.level}
                  </option>
                ))}
            </Input>
          </FormGroup>
          <small className="text-muted">
            If you lose, you forfeit your Pokémon to the winner.
          </small>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowChallenge(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmChallenge}>
            Race!
          </Button>
        </ModalFooter>
      </Modal>

      {/* RACING ANIMATION OVERLAY */}
      {showRaceAnimation && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 9999 }}
        >
          <Card style={{ maxWidth: 500, width: "90%", background: "#1d222e" }}>
            <CardBody>
              <h5 className="mb-4 text-center">The Race is On!</h5>
              <div
                className="d-flex justify-content-around align-items-end"
                style={{ height: 100 }}
              >
                {/* Challenger Pokemon */}
                <motion.img
                  src={raceMinipicTrack || "/img/placeholder.png"}
                  alt="Your Pokemon"
                  style={{ width: 60, height: "auto" }}
                  initial={{ x: 0 }}
                  animate={{
                    x: racingResult
                      ? racingResult.message.includes("Congrats!")
                        ? 200
                        : 50
                      : 50,
                  }}
                  transition={{ duration: 2 }}
                  onAnimationComplete={handleAnimationComplete}
                />

                {/* Racetrack Pokemon */}
                <motion.img
                  src={raceMinipicTrack || "/img/placeholder.png"}
                  alt="Track Pokemon"
                  style={{ width: 60, height: "auto" }}
                  initial={{ x: 0 }}
                  animate={{
                    x: racingResult
                      ? racingResult.message.includes("Unlucky!")
                        ? -200
                        : -50
                      : -50,
                  }}
                  transition={{ duration: 2 }}
                />
              </div>

              {/* After animation, show the result */}
              {racingResult && (
                <div className="mt-4 text-center">
                  <p>{racingResult.message}</p>
                  <Button color="secondary" onClick={closeRacingOverlay}>
                    Close
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* POPUP OVERLAY (instead of alert) */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px",
            background: popupColor,
            color: "white",
            zIndex: 9999,
            border: "2px solid white",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            textAlign: "center",
            maxWidth: "400px",
            width: "90%",
          }}
        >
          {popupImg && (
            <img
              src={popupImg}
              alt="Popup"
              style={{
                width: "120px",
                margin: "0 auto 10px auto",
                display: "block",
              }}
            />
          )}
          <p style={{ margin: "10px 0" }}>{popupMessage}</p>
          <button
            style={{
              background: "transparent",
              color: "#fff",
              border: "1px solid #fff",
              borderRadius: "4px",
              padding: "4px 10px",
              cursor: "pointer",
            }}
            onClick={() => setShowPopup(false)}
          >
            Close
          </button>
        </div>
      )}
    </React.Fragment>
  );
};

export default Racetracks;
